import { Vector2 } from 'three';
import { setup, assign, fromPromise } from 'xstate';
import {
  type PointerDownEv,
  type EventTypes,
  type PointerMoveEv,
  type ZoomChangeEv,
} from '@/state-machine/property-selector/event-types';
import { type Point } from '@/types';

export const MAX_ZOOM = 5;
export const MIN_ZOOM = 1;

const initialContext = {
  shapes: [] as Point[][],
  localShapes: [] as Point[][],
  newShape: [] as Point[],
  nextPoint: null as Point | null,
  zoomLevel: MIN_ZOOM,
};
export const input = {
  shapes: [] as Point[][],
};
export const saveShapeInput = async (args: { input: { shape: Point[] } }) => {};
const factory = setup({
  types: {
    context: initialContext,
    input,
    events: {} as EventTypes,
  },
  actors: {
    saveShape: fromPromise(saveShapeInput),
  },
  actions: {
    setCursorCrosshair: () => {
      if (typeof window !== 'undefined') {
        const canvas = document.querySelector('#draw-canvas') as HTMLCanvasElement;
        canvas.classList.add('cursor-crosshair');
        canvas.classList.remove('cursor-grab', 'cursor-grabbing', 'cursor-pointer');
      }
    },
    setCursorGrab: () => {
      if (typeof window !== 'undefined') {
        const canvas = document.querySelector('#draw-canvas') as HTMLCanvasElement;
        canvas.classList.add('cursor-grab');
        canvas.classList.remove('cursor-crosshair', 'cursor-grabbing', 'cursor-pointer');
      }
    },
    setCursorGrabbing: () => {
      if (typeof window !== 'undefined') {
        const canvas = document.querySelector('#draw-canvas') as HTMLCanvasElement;
        canvas.classList.add('cursor-grabbing');
        canvas.classList.remove('cursor-crosshair', 'cursor-grab', 'cursor-pointer');
      }
    },
    setCursorPointer: () => {
      if (typeof window !== 'undefined') {
        const canvas = document.querySelector('#draw-canvas') as HTMLCanvasElement;
        canvas.classList.add('cursor-pointer');
        canvas.classList.remove('cursor-crosshair', 'cursor-grab', 'cursor-grabbing');
      }
    },
    setCursorDefault: () => {
      if (typeof window !== 'undefined') {
        const canvas = document.querySelector('#draw-canvas') as HTMLCanvasElement;
        canvas.classList.remove(
          'cursor-crosshair',
          'cursor-grab',
          'cursor-grabbing',
          'cursor-pointer',
        );
      }
    },
    resetContext: assign(() => ({
      newShape: initialContext.newShape,
      nextPoint: initialContext.nextPoint,
    })),
    addPoint: assign(({ context, event }) => {
      const ev = event as PointerDownEv;
      return { newShape: [...context.newShape, ev.point] };
    }),
    previewLine: assign(({ context, event }) => {
      const ev = event as PointerMoveEv;
      let nextPoint = context.nextPoint;
      if (context.newShape.length > 0) {
        nextPoint = ev.point;
      }
      return { nextPoint };
    }),
    closeShape: assign(({ context }) => {
      return {
        localShapes: [...context.localShapes, context.newShape],
        newShape: initialContext.newShape,
        nextPoint: initialContext.nextPoint,
      };
    }),
    deleteShape: assign(({ context }) => {
      const localShapes = context.localShapes.slice(0, -1);
      return { localShapes };
    }),
    zoomChange: assign(({ event }) => ({
      zoomLevel: (event as ZoomChangeEv).zoomLevel,
    })),
  },
  guards: {
    shouldCloseShape: ({ context }, { point }: { point: Point }) => {
      if (context.newShape.length > 1) {
        const first = new Vector2(context.newShape[0].x, context.newShape[0].y);
        const last = new Vector2(point.x, point.y);
        const distance = first.distanceTo(last);
        return distance < 0.02;
      }
      return false;
    },
  },
});

export const machine = factory.createMachine({
  id: 'home-selector',
  context: initialContext,
  initial: 'idle',
  on: {
    ZOOM_CHANGE: { actions: ['zoomChange'] },
  },
  states: {
    saving: {
      invoke: {
        id: 'saveShape',
        src: 'saveShape',
        input: ({ context }) => {
          const localShapes = context.localShapes;
          return {
            shape: localShapes[localShapes.length - 1],
          };
        },
        onDone: 'idle',
        onError: 'idle',
      },
    },
    idle: {
      entry: ['resetContext', 'setCursorDefault'],
      on: {
        DRAW: 'drawing',
        PAN: 'panning',
        POINTER_OVER: { actions: ['setCursorPointer'] },
        POINTER_OUT: { actions: ['setCursorDefault'] },
        DELETE_SHAPE: { target: 'saving', actions: ['deleteShape'] },
      },
    },
    drawing: {
      entry: ['setCursorCrosshair'],
      on: {
        SELECT: 'idle',
        PAN: 'panning',
        RESET: { actions: ['resetContext'] },
        POINTER_DOWN: [
          {
            actions: ['closeShape'],
            guard: {
              type: 'shouldCloseShape',
              params: ({ event }) => ({ point: (event as PointerDownEv).point }),
            },
            target: 'saving',
          },
          { actions: ['addPoint'] },
        ],
        POINTER_MOVE: { actions: ['previewLine'] },
      },
    },
    panning: {
      entry: ['setCursorGrab'],
      on: {
        SELECT: 'idle',
        DRAW: 'drawing',
        POINTER_DOWN: { actions: ['setCursorGrabbing'] },
        POINTER_UP: { actions: ['setCursorGrab'] },
      },
    },
  },
});
